import React, { useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faComment, faShare, faUser, faPaperPlane, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../lib/api';
import { useToast } from '../common/Toast';
import { logger } from '../../lib/logger';

const FeedPost = memo(({ post, onUpdatePost }) => {
  const authUser = useSelector((state) => state.auth.user);
  const toast = useToast();
  const [isLiked, setIsLiked] = useState(post.isLiked || false);
  const [likesCount, setLikesCount] = useState(post.likesCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(post.comments || []);
  const [commentText, setCommentText] = useState('');
  const [shareSuccess, setShareSuccess] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);

  const handleLike = async () => {
    if (!authUser?.id || isLiking) return;
    
    setIsLiking(true);
    const newIsLiked = !isLiked;
    
    setIsLiked(newIsLiked);
    setLikesCount(prev => newIsLiked ? prev + 1 : prev - 1);

    try {
      if (newIsLiked) {
        await api.posts.like(post.id);
      } else {
        await api.posts.unlike(post.id);
      }

      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          isLiked: newIsLiked,
          likesCount: newIsLiked ? likesCount + 1 : likesCount - 1
        });
      }
    } catch (error) {
      logger.error('Error updating like:', error);
      setIsLiked(!newIsLiked);
      setLikesCount(prev => newIsLiked ? prev - 1 : prev + 1);
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !authUser?.id || isCommenting) return;

    setIsCommenting(true);
    const trimmedText = commentText.trim();

    try {
      const newCommentData = await api.posts.addComment(post.id, trimmedText);

      const newComment = {
        id: newCommentData.id,
        user: authUser.name || 'You',
        avatar: authUser.avatar_url || null,
        text: newCommentData.content,
        timestamp: 'Just now'
      };
      
      const updatedComments = [...comments, newComment];
      setComments(updatedComments);
      setCommentText('');
      
      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          comments: updatedComments,
          commentsCount: updatedComments.length
        });
      }
    } catch (error) {
      logger.error('Error adding comment:', error);
      toast.error('Failed to add comment. Please try again.');
    } finally {
      setIsCommenting(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: post.title || 'Check out this run!',
      text: post.content || 'Amazing run from ' + post.user?.name,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
    }
  };

  return (
    <div className="scrapbook-container mb-6 animate-bounce-in">
      <div className="flex items-center space-x-3 mb-4">
        {post.user?.id ? (
          <Link 
            to={`/user/${post.user.id}`}
            className="w-12 h-12 rounded-full bg-gradient-vibe flex items-center justify-center text-white font-bold text-xl overflow-hidden hover:ring-2 hover:ring-primary-pink hover:ring-offset-2 transition-all"
          >
            {post.user?.avatar ? (
              <img src={post.user.avatar} alt="User avatar" className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon icon={faUser} className="text-white" />
            )}
          </Link>
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-vibe flex items-center justify-center text-white font-bold text-xl overflow-hidden">
            {post.user?.avatar ? (
              <img src={post.user.avatar} alt="User avatar" className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon icon={faUser} className="text-white" />
            )}
          </div>
        )}
        <div className="flex-1">
          {post.user?.id ? (
            <Link 
              to={`/user/${post.user.id}`}
              className="font-heading font-semibold text-gray-900 hover:text-primary-pink transition-colors"
            >
              {post.user?.name || 'Anonymous Runner'}
            </Link>
          ) : (
            <h3 className="font-heading font-semibold text-gray-900">
              {post.user?.name || 'Anonymous Runner'}
            </h3>
          )}
          <p className="text-sm text-gray-500">
            {post.timestamp || '2 hours ago'}
          </p>
        </div>
      </div>

      <div className="mb-4">
        {post.type === 'run' && (
          <div className="bg-gradient-vibe-subtle rounded-2xl p-4 mb-3">
            <div className="flex items-center space-x-2 mb-2">
              <h4 className="font-heading font-semibold text-gray-900">
                {post.title || 'Morning Run'}
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-bold text-lg text-primary-pink">
                  {post.distance || '5.2'}
                </div>
                <div className="text-xs text-gray-600">km</div>
              </div>
              <div>
                <div className="font-bold text-lg text-secondary-blue">
                  {post.time || '24:15'}
                </div>
                <div className="text-xs text-gray-600">time</div>
              </div>
              <div>
                <div className="font-bold text-lg text-primary-yellow">
                  {post.pace || '4:39'}
                </div>
                <div className="text-xs text-gray-600">pace</div>
              </div>
            </div>
          </div>
        )}
        
        {post.content && (
          <p className="text-gray-800 font-body text-body mb-3">
            {post.content}
          </p>
        )}
        
        {post.image && (
          <div className="rounded-2xl overflow-hidden mb-3">
            <img 
              src={post.image} 
              alt="Post content" 
              className="w-full h-64 object-cover"
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleLike}
            disabled={isLiking || !authUser}
            className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-all hover:scale-[1.05] active:scale-[0.95] ${
              isLiked 
                ? 'bg-primary-pink text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${(isLiking || !authUser) ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <FontAwesomeIcon 
              icon={isLiking ? faSpinner : faHeart} 
              className={`w-4 h-4 ${isLiked ? 'fill-current' : ''} ${isLiking ? 'animate-spin' : ''}`} 
            />
            <span className="text-sm font-medium">{likesCount}</span>
          </button>
          
          <button
            onClick={() => setShowComments(!showComments)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-all hover:scale-[1.05] active:scale-[0.95] ${
              showComments 
                ? 'bg-secondary-blue text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <FontAwesomeIcon icon={faComment} className="w-4 h-4" />
            <span className="text-sm font-medium">{comments.length}</span>
          </button>
        </div>
        
        <button
          onClick={handleShare}
          className="flex items-center space-x-2 px-3 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 hover:scale-[1.05] active:scale-[0.95] transition-all"
        >
          <FontAwesomeIcon icon={shareSuccess ? faCheck : faShare} className="w-4 h-4" />
          <span className="text-sm font-medium">{shareSuccess ? 'Copied!' : 'Share'}</span>
        </button>
      </div>

      {showComments && (
        <div className="pt-4 border-t border-gray-100 mt-4">
          <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
            {comments.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className="flex space-x-3"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-vibe flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                    {comment.avatar ? (
                      <img src={comment.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      comment.user[0]
                    )}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm text-gray-900">
                        {comment.user}
                      </span>
                      <span className="text-xs text-gray-500">
                        {comment.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleCommentSubmit} className="flex items-center space-x-2">
            <div className="flex-1">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={authUser ? "Add a comment..." : "Login to comment"}
                disabled={!authUser || isCommenting}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
            <button
              type="submit"
              disabled={!commentText.trim() || !authUser || isCommenting}
              className={`px-4 py-2 rounded-xl font-semibold transition-all hover:scale-[1.05] active:scale-[0.95] ${
                commentText.trim() && authUser && !isCommenting
                  ? 'bg-gradient-vibe text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <FontAwesomeIcon 
                icon={isCommenting ? faSpinner : faPaperPlane} 
                className={`w-4 h-4 ${isCommenting ? 'animate-spin' : ''}`} 
              />
            </button>
          </form>
        </div>
      )}
    </div>
  );
});

export default FeedPost;
